import { PLACEMENT_PRICING } from '@/lib/pricingConfig';

export type RegionDefinition = {
  id: string;
  name: string;
  cities: string[];
  households: number;
};

export type PlacementDefinition = {
  id: 'standard' | 'half_page' | 'ifc' | 'ibc' | 'bc' | 'fc' | 'flyer_insert' | 'premium_flyer_insert';
  name: string;
  position: string;
  description: string;
  oneIssue: number;
  threeIssue: number;
  capacity: number;
  defaultFilled: number;
  premium: boolean;
  features: string[];
  color: 'brand' | 'sky' | 'violet' | 'amber' | 'gradient';
  mockupPosition: 'interior' | 'inside-front' | 'inside-back' | 'back' | 'front';
  isFlyerInsert?: boolean;
};

export const PLACEMENT_PRICING_CONFIG = PLACEMENT_PRICING;

export const FLORIDA_REGIONS: RegionDefinition[] = [
  { id: 'fl-jax', name: 'Jacksonville–North Florida', cities: ['Jacksonville', 'St. Augustine', 'Fernandina Beach', 'Orange Park'], households: 1000 },
  { id: 'fl-gnv', name: 'Gainesville–Ocala', cities: ['Gainesville', 'Ocala', 'High Springs', 'Alachua'], households: 1000 },
  { id: 'fl-orl', name: 'Orlando / Central FL North', cities: ['Orlando', 'Sanford', 'Kissimmee', 'Apopka'], households: 1000 },
  { id: 'fl-lkl', name: 'Lakeland / Polk', cities: ['Lakeland', 'Winter Haven', 'Bartow', 'Haines City'], households: 1000 },
  { id: 'fl-pin', name: 'Pinellas / St. Pete–Clearwater', cities: ['St. Petersburg', 'Clearwater', 'Largo', 'Dunedin'], households: 1000 },
  { id: 'fl-tpa', name: 'Tampa / East Hillsborough–Pasco', cities: ['Tampa', 'Brandon', 'Wesley Chapel', 'New Port Richey'], households: 1000 },
  { id: 'fl-sar', name: 'Sarasota–Bradenton', cities: ['Sarasota', 'Bradenton', 'Venice', 'Englewood'], households: 1000 },
  { id: 'fl-npl', name: 'Naples / Fort Myers', cities: ['Naples', 'Fort Myers', 'Bonita Springs', 'Cape Coral'], households: 1000 },
  { id: 'fl-mia', name: 'South Florida', cities: ['Miami', 'Fort Lauderdale', 'West Palm Beach', 'Boca Raton'], households: 1000 },
  { id: 'fl-pan', name: 'Florida Panhandle', cities: ['Pensacola', 'Panama City', 'Tallahassee', 'Destin'], households: 1000 },
  { id: 'fl-dyt', name: 'Daytona Beach / Volusia', cities: ['Daytona Beach', 'Deltona', 'Ormond Beach', 'New Smyrna Beach'], households: 1000 },
  { id: 'fl-spc', name: 'Space Coast / Brevard', cities: ['Melbourne', 'Titusville', 'Cocoa', 'Palm Bay'], households: 1000 },
  { id: 'fl-key', name: 'Florida Keys / Monroe', cities: ['Key West', 'Marathon', 'Key Largo', 'Islamorada'], households: 1000 },
];

export const PUERTO_RICO_REGIONS: RegionDefinition[] = [
  { id: 'pr-sju', name: 'Metro San Juan', cities: ['San Juan', 'Bayamón', 'Carolina', 'Guaynabo', 'Toa Baja'], households: 1000 },
  { id: 'pr-out', name: 'Outside Metro', cities: ['Ponce', 'Mayagüez', 'Caguas', 'Arecibo', 'Humacao'], households: 1000 },
  { id: 'pr-nor', name: 'North / West PR', cities: ['Arecibo', 'Aguadilla', 'Mayagüez', 'Isabela'], households: 1000 },
  { id: 'pr-sou', name: 'South PR', cities: ['Ponce', 'Guayama', 'Salinas', 'Coamo'], households: 1000 },
];

export const ALL_REGIONS: RegionDefinition[] = [...FLORIDA_REGIONS, ...PUERTO_RICO_REGIONS];

export const REGION_NAME_BY_ID = ALL_REGIONS.reduce<Record<string, string>>((acc, region) => {
  acc[region.id] = region.name;
  return acc;
}, {});

export const PLACEMENTS_CATALOG: PlacementDefinition[] = [
  {
    id: 'standard',
    name: 'Standard Inside Page',
    position: 'Pages 3–9',
    description: 'Full-page interior placement visible to every reader. 9 sellable slots available per region. Best for maximum reach at the lowest entry price.',
    oneIssue: 300,
    threeIssue: 200,
    capacity: 9,
    defaultFilled: 4,
    premium: false,
    features: ['Full page (8.5×5.5)', '9 slots per region', 'Multi-region discounts', 'Standard turnaround'],
    color: 'brand',
    mockupPosition: 'interior',
  },
  {
    id: 'half_page',
    name: 'Half Page (Coupon Book)',
    position: 'Pages 3–9 — Half Page',
    description: 'Half-page coupon book placement. Ideal for coupon-style promotions. Multiple slots available per region at a lower entry price.',
    oneIssue: 170,
    threeIssue: 55,
    capacity: 9,
    defaultFilled: 0,
    premium: false,
    features: ['Half page (8.5×2.75)', '9 slots per region', 'Coupon book format', 'Standard turnaround'],
    color: 'brand',
    mockupPosition: 'interior',
  },
  {
    id: 'ifc',
    name: 'Inside Front Cover',
    position: 'Page 2',
    description: 'First ad page readers see when they open the book. High-impact position with immediate visibility. Only 1 slot per region per issue.',
    oneIssue: 650,
    threeIssue: 500,
    capacity: 1,
    defaultFilled: 1,
    premium: true,
    features: ['First interior page', '1 slot per region', 'Premium visibility', 'Priority processing'],
    color: 'sky',
    mockupPosition: 'inside-front',
  },
  {
    id: 'ibc',
    name: 'Inside Back Cover',
    position: 'Page 11',
    description: 'Second-to-last page — a natural resting point for readers. Strong recall position. 1 exclusive slot per region.',
    oneIssue: 650,
    threeIssue: 500,
    capacity: 1,
    defaultFilled: 0,
    premium: true,
    features: ['Near-last interior page', '1 slot per region', 'High recall position', 'Priority processing'],
    color: 'violet',
    mockupPosition: 'inside-back',
  },
  {
    id: 'bc',
    name: 'Back Cover',
    position: 'Page 12 — Exterior',
    description: 'The most visible exterior position. Always face-up when the book is set down. Maximum passive impressions. 1 exclusive slot per region.',
    oneIssue: 1750,
    threeIssue: 1500,
    capacity: 1,
    defaultFilled: 0,
    premium: true,
    features: ['Exterior back cover', '1 slot per region', 'Max passive visibility', 'White-glove service'],
    color: 'amber',
    mockupPosition: 'back',
  },
  {
    id: 'fc',
    name: 'Front Cover',
    position: 'Page 1 — Exterior',
    description: 'The crown jewel of ZipAdBook. Your brand IS the cover. Unmatched brand recognition with every single distribution. 1 exclusive slot per region.',
    oneIssue: 2200,
    threeIssue: 2000,
    capacity: 1,
    defaultFilled: 1,
    premium: true,
    features: ['Exterior front cover', '1 slot per region', 'Dominant brand position', 'Dedicated account manager'],
    color: 'gradient',
    mockupPosition: 'front',
  },
  {
    id: 'flyer_insert',
    name: 'Flyer Insert',
    position: 'Inserted into every book',
    description: 'A printed flyer physically inserted into every coupon book distributed in the region. Available even when the book is sold out. Does not consume a book page.',
    oneIssue: 500,
    threeIssue: 400,
    capacity: 3,
    defaultFilled: 0,
    premium: false,
    features: ['Physical flyer insert', 'Does not use book pages', 'Available when book is full', '1,000 copies per region'],
    color: 'brand',
    mockupPosition: 'interior',
    isFlyerInsert: true,
  },
  {
    id: 'premium_flyer_insert',
    name: 'Premium Flyer Insert',
    position: 'Inserted into every book — Premium',
    description: 'A premium printed flyer inserted into every coupon book. Higher-visibility stock, exclusive positioning within the insert stack.',
    oneIssue: 750,
    threeIssue: 600,
    capacity: 3,
    defaultFilled: 0,
    premium: true,
    features: ['Premium flyer insert', 'Priority insert position', 'Does not use book pages', 'White-glove service'],
    color: 'amber',
    mockupPosition: 'interior',
    isFlyerInsert: true,
  },
];

export const PLACEMENT_NAME_BY_ID = PLACEMENTS_CATALOG.reduce<Record<string, string>>((acc, placement) => {
  acc[placement.id] = placement.name;
  return acc;
}, {});
